import { DatePicker, Form, Input, Modal, message } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import CertificateAPI from '../../certificate/CertificateAPI';
import Course from '../types/Course';

type FurboCertificatePreviewModalProps = {
  course: Course;
  onClose: () => void;
};

type FurboPreviewFormValues = {
  attendeeName: string;
  birthDate: Dayjs;
  birthPlace: string;
  classDates: [Dayjs, Dayjs];
};

const FurboCertificatePreviewModal: React.FC<
  FurboCertificatePreviewModalProps
> = ({ course, onClose }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<FurboPreviewFormValues>();
  const [downloading, setDownloading] = useState(false);

  const submit = async (values: FurboPreviewFormValues) => {
    const [startDate, endDate] = values.classDates;

    setDownloading(true);
    try {
      await CertificateAPI.getFurboCertificatePreview(
        course.id,
        t('components.CertificatePreviewModal.fileName', {
          attendeeName: values.attendeeName,
          classDate: startDate.format('YYYY-MM-DD'),
          courseName: course.name,
        }),
        {
          attendeeName: values.attendeeName,
          birthDate: values.birthDate.format('YYYY-MM-DD'),
          birthPlace: values.birthPlace,
          startDate: startDate.format('YYYY-MM-DD'),
          endDate: endDate.format('YYYY-MM-DD'),
        },
      );
      onClose();
    } catch {
      message.error(t('components.CertificatePreviewModal.previewFailed'));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal
      maskClosable={false}
      okButtonProps={{ loading: downloading }}
      okText={t('components.CertificatePreviewModal.download')}
      open
      title={
        <div>
          <h3>
            {t('components.CertificatePreviewModal.title', {
              courseName: course.name,
            })}
          </h3>
          <div className="font-normal text-gray-700 -mt-2">{course.name}</div>
        </div>
      }
      width={600}
      onCancel={onClose}
      onOk={form.submit}>
      <Form
        form={form}
        initialValues={{ classDates: [dayjs(), dayjs()] }}
        layout="vertical"
        onFinish={submit}>
        <Form.Item
          name="attendeeName"
          label={t('components.CertificatePreviewModal.attendeeName')}
          rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item
          name="birthDate"
          label={t('components.CertificatePreviewModal.birthDate')}
          rules={[{ required: true }]}>
          <DatePicker allowClear={false} />
        </Form.Item>
        <Form.Item
          name="birthPlace"
          label={t('components.CertificatePreviewModal.birthPlace')}
          rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item
          name="classDates"
          label={t('components.CertificatePreviewModal.classDate')}
          rules={[{ required: true }]}>
          <DatePicker.RangePicker allowClear={false} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default FurboCertificatePreviewModal;
